/**
 * Default Autodesk Viewer measure display for FileShare (client preference):
 * feet + fractional inches, e.g. 6' - 8".
 *
 * Supported unit strings (APS MeasureExtension):
 * 'decimal-ft' | 'ft-and-fractional-in' | 'ft-and-decimal-in' |
 * 'decimal-in' | 'fractional-in' | 'm' | 'cm' | 'mm' | 'm-and-cm'
 */
export const DEFAULT_MEASURE_UNITS = 'ft-and-fractional-in';

/** Fractional-inch precision index: 0=1", 1=1/2, 2=1/4, 3=1/8, 4=1/16, 5=1/32, 6=1/64 */
export const DEFAULT_MEASURE_PRECISION = 4;

function applyUnitsToExtension(ext, units, precision) {
  if (!ext) return false;
  try {
    if (ext.sharedMeasureConfig) {
      ext.sharedMeasureConfig.units = units;
      if (typeof precision === 'number') {
        ext.sharedMeasureConfig.precision = precision;
      }
    }
    if (typeof ext.setUnits === 'function') {
      ext.setUnits(units);
    }
    if (typeof precision === 'number' && typeof ext.setPrecision === 'function') {
      ext.setPrecision(precision);
    }
    if (ext.measureTool) {
      if (typeof ext.measureTool.setUnits === 'function') {
        ext.measureTool.setUnits(units);
      }
      if (typeof precision === 'number' && typeof ext.measureTool.setPrecision === 'function') {
        ext.measureTool.setPrecision(precision);
      }
    }
    return true;
  } catch (err) {
    console.warn('Could not apply Autodesk measure units:', err);
    return false;
  }
}

/**
 * Set default measure units on a GuiViewer3D instance after the model loads.
 * Re-applies when Autodesk.Measure loads (toolbar may load the extension lazily).
 */
export function applyDefaultMeasureUnits(
  viewer,
  units = DEFAULT_MEASURE_UNITS,
  precision = DEFAULT_MEASURE_PRECISION
) {
  if (!viewer || typeof viewer.getExtension !== 'function') return;

  const apply = () => applyUnitsToExtension(viewer.getExtension('Autodesk.Measure'), units, precision);

  if (!apply() && typeof viewer.loadExtension === 'function') {
    viewer.loadExtension('Autodesk.Measure').then((ext) => {
      applyUnitsToExtension(ext, units, precision);
    }).catch((err) => {
      console.warn('Could not load Autodesk.Measure extension:', err);
    });
  }

  const Autodesk = typeof window !== 'undefined' ? window.Autodesk : null;
  const loadedEvent = Autodesk?.Viewing?.EXTENSION_LOADED_EVENT;
  if (loadedEvent && typeof viewer.addEventListener === 'function') {
    const onLoaded = (event) => {
      if (event?.extensionId === 'Autodesk.Measure') {
        apply();
      }
    };
    viewer.addEventListener(loadedEvent, onLoaded);
    // Stash remover so callers can clean up if needed
    viewer.__asrosyncMeasureUnitsCleanup = () => {
      try {
        viewer.removeEventListener(loadedEvent, onLoaded);
      } catch {
        /* ignore */
      }
    };
  }
}
